/**
 * Encompass Investor Template Types
 *
 * Types for syncing and displaying Encompass Investor Templates data
 * which includes 16 contact types per template.
 */
import type { ProductType } from '@/types/investor';

/**
 * The 16 contact types available in Encompass Investor Templates.
 *
 * Canonical definition lives in the edge-safe goodbye-letter-types module so
 * the `send-goodbye-letter` edge function can import it without crossing the
 * shared→src boundary. Re-exported here so existing `@/types/investor-encompass`
 * callers compile unchanged.
 */
export type { EncompassContactType } from '../../supabase/functions/_shared/goodbye-letter-types';

/**
 * Human-readable labels for contact types
 */
export const CONTACT_TYPE_LABELS: Record<EncompassContactType, string> = {
  NoteDelivery: 'Note Delivery',
  LoanDelivery: 'Loan Delivery',
  ShipTo: 'Ship To',
  CustomerService: 'Customer Service',
  TrailingDocs: 'Trailing Documents',
  MortgageInsurance: 'Mortgage Insurance',
  Payment: 'Payment',
  Insurance: 'Insurance',
  Assignment: 'Assignment',
  TaxNotice: 'Tax Notice',
  Correspondence: 'Correspondence',
  Secondary: 'Secondary',
  Generic1: 'Generic 1',
  Generic2: 'Generic 2',
  Generic3: 'Generic 3',
  Generic4: 'Generic 4'
};

/**
 * Contact type groupings for UI display
 */
export const CONTACT_TYPE_GROUPS: Record<string, EncompassContactType[]> = {
  delivery: ['NoteDelivery', 'LoanDelivery', 'ShipTo', 'TrailingDocs'],
  servicing: ['CustomerService', 'Payment', 'TaxNotice'],
  insurance: ['MortgageInsurance', 'Insurance'],
  other: ['Assignment', 'Correspondence', 'Secondary']
};

/**
 * Human-readable labels for contact groups
 */
export const CONTACT_GROUP_LABELS: Record<string, string> = {
  delivery: 'Delivery Contacts',
  servicing: 'Servicing Contacts',
  insurance: 'Insurance Contacts',
  other: 'Other Contacts'
};

/**
 * Contact information structure from Encompass
 */
export interface EncompassTemplateContact {
  id?: string;
  templateId?: string;
  organizationId?: string;
  contactType: EncompassContactType;
  investorName?: string | null;
  contactName?: string | null;
  department?: string | null;
  attentionTo?: string | null;
  addressStreet1?: string | null;
  addressStreet2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  phone?: string | null;
  fax?: string | null;
  email?: string | null;
  website?: string | null;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Main investor template structure
 */
export interface InvestorEncompassTemplate {
  id: string;
  organizationId: string;
  investorId: string;

  // Encompass identifiers
  encompassTemplateId: string;

  // Template metadata
  templateName: string;
  templatePath?: string | null;
  bulkSale?: boolean | null;
  deliveryTimeFrame?: number | null;
  purchaserType?: string | null;
  purchaserTypeValue?: string | null;
  investorCode?: string | null;
  encompassInvestorName?: string | null;
  encompassInvestorId?: string | null;

  // Dates
  servicingTransferDate?: string | null;
  commitmentNumber?: string | null;
  commitmentDate?: string | null;
  commitmentExpirationDate?: string | null;
  targetDeliveryDate?: string | null;
  lockPeriodDays?: number | null;
  isPublic?: boolean | null;

  // Contacts (populated from related table)
  contacts?: EncompassTemplateContact[];

  // Product scope (populated from investor_encompass_template_product_assignments)
  applicableProducts?: ProductType[];
  assignments?: InvestorTemplateProductAssignment[];

  // Sync metadata
  lastSyncedAt: string;
  syncStatus: 'synced' | 'pending' | 'error';
  syncError?: string | null;

  // Raw data for display flexibility
  rawTemplateData?: Record<string, unknown> | null;

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

/**
 * Sync request types
 */
export interface SyncInvestorTemplatesRequest {
  investorId?: string;              // Sync specific investor
  encompassTemplateId?: string;     // Sync specific template
  templateProductScopes?: Array<{
    encompassTemplateId: string;
    products: ProductType[];
  }>;
  forceRefresh?: boolean;           // Force re-fetch even if recently synced
}

/**
 * Sync response types
 */
export interface SyncInvestorTemplatesResponse {
  success: boolean;
  synced: number;
  errors: Array<{
    templateId: string;
    error: string;
  }>;
  message?: string;
  templates?: InvestorEncompassTemplate[];
}

/**
 * Database row types (snake_case for Supabase)
 */
export interface InvestorEncompassTemplateRow {
  id: string;
  organization_id: string;
  investor_id: string;
  encompass_template_id: string;
  template_name: string;
  template_path: string | null;
  bulk_sale: boolean | null;
  delivery_time_frame: number | null;
  purchaser_type: string | null;
  purchaser_type_value: string | null;
  investor_code: string | null;
  encompass_investor_name: string | null;
  encompass_investor_id: string | null;
  servicing_transfer_date: string | null;
  commitment_number: string | null;
  commitment_date: string | null;
  commitment_expiration_date: string | null;
  target_delivery_date: string | null;
  lock_period_days: number | null;
  is_public: boolean | null;
  last_synced_at: string;
  sync_status: 'synced' | 'pending' | 'error';
  sync_error: string | null;
  raw_template_data: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface InvestorTemplateProductAssignment {
  id: string;
  organizationId: string;
  investorId: string;
  templateId: string;
  product: ProductType;
  source: string;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InvestorTemplateProductAssignmentRow {
  id: string;
  organization_id: string;
  investor_id: string;
  template_id: string;
  product: ProductType;
  source: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface InvestorEncompassTemplateContactRow {
  id: string;
  organization_id: string;
  template_id: string;
  contact_type: EncompassContactType;
  investor_name: string | null;
  contact_name: string | null;
  department: string | null;
  attention_to: string | null;
  address_street1: string | null;
  address_street2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  fax: string | null;
  email: string | null;
  website: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}
